import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { User, LoginCredentials, RegisterData } from "../types";
import { authAPI } from "../services/api";
import { useAlert } from "../contexts/AlertContext";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<boolean>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<boolean>;
  resetPassword: (
    email: string,
    otp: string,
    password: string,
    password_confirmation: string
  ) => Promise<boolean>;
  verifyEmail: (otp: string) => Promise<boolean>;
  resendVerification: () => Promise<boolean>;
  updateUser: (userData: Partial<User>) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { addAlert } = useAlert();

  // Check if user is authenticated on app start
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      await refreshUser();
    } catch (error) {
      // Token is invalid, clear it
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const response = await authAPI.me();
      const userData = response.data;

      // Ensure numeric fields are numbers
      const processedUser = {
        ...userData,
        ledger_balance: Number(userData.ledger_balance) || 0,
        stake_balance: Number(userData.stake_balance) || 0,
        bonus_balance: Number(userData.bonus_balance) || 0,
        earnings_balance: Number(userData.earnings_balance) || 0,
        // Ensure wallets array exists
        wallets: userData.wallets || [],
      };

      setUser(processedUser);
      setIsAuthenticated(true);
      localStorage.setItem("user", JSON.stringify(processedUser));
    } catch (error) {
      throw new Error("Failed to fetch user data");
    }
  };

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await authAPI.login(credentials);
      const { access_token, user: userData } = response.data;

      // Store token
      localStorage.setItem("access_token", access_token);

      // Process user data
      const processedUser = {
        ...userData,
        ledger_balance: Number(userData.ledger_balance) || 0,
        stake_balance: Number(userData.stake_balance) || 0,
        bonus_balance: Number(userData.bonus_balance) || 0,
      };

      setUser(processedUser);
      setIsAuthenticated(true);
      localStorage.setItem("user", JSON.stringify(processedUser));

      addAlert({ type: "success", message: "Login successful!" });
      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Login failed. Please try again.";
      addAlert({ type: "error", message: errorMessage });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await authAPI.register(userData);
      const { access_token, user: newUser } = response.data;

      // Store token and user data
      localStorage.setItem("access_token", access_token);

      // Process user data
      const processedUser = {
        ...newUser,
        ledger_balance: Number(newUser.ledger_balance) || 0,
        stake_balance: Number(newUser.stake_balance) || 0,
        bonus_balance: Number(newUser.bonus_balance) || 0,
      };

      setUser(processedUser);
      setIsAuthenticated(true);
      localStorage.setItem("user", JSON.stringify(processedUser));

      addAlert({
        type: "success",
        message: "Registration successful! Please verify your email.",
      });
      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Registration failed. Please try again.";
      addAlert({ type: "error", message: errorMessage });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const token = localStorage.getItem("access_token");
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setUser(null);
      setIsAuthenticated(false);
      addAlert({ type: "success", message: "Logged out successfully!" });
    }
  };

  const forgotPassword = async (email: string): Promise<boolean> => {
    try {
      await authAPI.forgotPassword({ email });
      addAlert({ type: "success", message: "OTP sent to your email!" });
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "Failed to send OTP";
      addAlert({ type: "error", message: errorMessage });
      return false;
    }
  };

  const resetPassword = async (
    email: string,
    otp: string,
    password: string,
    password_confirmation: string
  ): Promise<boolean> => {
    try {
      await authAPI.resetPassword({
        email,
        otp,
        password,
        password_confirmation,
      });
      addAlert({ type: "success", message: "Password reset successfully!" });
      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Password reset failed";
      addAlert({ type: "error", message: errorMessage });
      return false;
    }
  };

  const verifyEmail = async (otp: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await authAPI.verifyEmail({ otp });

      if (response.data.user) {
        // Update user data with the response
        const processedUser = {
          ...response.data.user,
          ledger_balance: Number(response.data.user.ledger_balance) || 0,
          stake_balance: Number(response.data.user.stake_balance) || 0,
          bonus_balance: Number(response.data.user.bonus_balance) || 0,
        };

        setUser(processedUser);
        localStorage.setItem("user", JSON.stringify(processedUser));
      } else {
        // Fallback: refresh user data
        await refreshUser();
      }

      addAlert({ type: "success", message: "Email verified successfully!" });
      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Email verification failed";
      addAlert({ type: "error", message: errorMessage });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const resendVerification = async (): Promise<boolean> => {
    try {
      await authAPI.resendVerification();
      addAlert({
        type: "success",
        message: "Verification code resent successfully!",
      });
      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to resend verification code";
      addAlert({ type: "error", message: errorMessage });
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    updateUser,
    refreshUser,
  };

  return React.createElement(AuthContext.Provider, { value }, children);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
